import { Moon, Sun, Monitor } from 'lucide-react'
import { useTheme, type ColorMode } from '@/contexts/ThemeContext'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

const MODE_OPTIONS: { value: ColorMode; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
]

/**
 * Simple theme mode toggle for the header.
 * Allows switching between system/light/dark modes.
 *
 * For full theme customization (colors, fonts, radius),
 * use ThemePanel (accessible via Cmd/Ctrl + Shift + T in dev).
 */
export function ThemeToggle() {
  const { theme, setTheme, resolvedMode } = useTheme()

  // Show the icon for the user's selected mode so "system" remains visible.
  const CurrentIcon =
    MODE_OPTIONS.find((option) => option.value === theme.mode)?.icon ?? Sun

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          title={
            theme.mode === 'system'
              ? `Theme: system (${resolvedMode})`
              : `Theme: ${theme.mode}`
          }
        >
          <CurrentIcon className="h-4 w-4" />
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {MODE_OPTIONS.map((option) => {
          const Icon = option.icon
          const isActive = theme.mode === option.value
          return (
            <DropdownMenuItem
              key={option.value}
              onClick={() => setTheme({ mode: option.value })}
              className={isActive ? 'bg-muted' : ''}
            >
              <Icon className="mr-2 h-4 w-4" />
              {option.label}
              {isActive && <span className="ml-auto text-xs text-muted-foreground">Active</span>}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
